import { useEffect, useState } from 'react';
import { Menu, X, Globe, ChevronDown } from 'lucide-react';
import { useLocation, Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { getLanguageUrl } from '@/lib/utils';

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const location = useLocation();
  const { language, t } = useLanguage();
  
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  // Close language dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (isLangDropdownOpen) {
        setIsLangDropdownOpen(false);
      }
    };
    
    if (isLangDropdownOpen) {
      // Delay to prevent immediate close on the same click
      setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 0);
    }
    
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isLangDropdownOpen]);

  const homeUrl = getLanguageUrl('/', language);

  const navItems = [
    { label: t('nav.home'), href: homeUrl },
    { label: t('nav.registo'), href: getLanguageUrl('/registo-yango-motorista-maputo', language) },
    { label: t('nav.cadastro'), href: getLanguageUrl('/cadastro-motorista-yango', language) },
    { label: t('nav.requisitos'), href: getLanguageUrl('/requisitos-motorista-yango', language) },
    { label: t('nav.download'), href: getLanguageUrl('/baixar-yango-pro-maputo', language) },
    { label: t('nav.recarga'), href: getLanguageUrl('/recarga-automatica-yango-pro-emola-mpesa', language) },
    { label: t('nav.blog'), href: getLanguageUrl('/blog', language) },
    { label: t('nav.sobreNos'), href: getLanguageUrl('/sobre-nos', language) },
  ];

  const handleNavClick = () => {
    setIsMenuOpen(false);
  };

  return (
    <>
      {/* Skip navigation link for keyboard users */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[150] focus:bg-primary focus:text-white focus:px-4 focus:py-2 focus:rounded"
      >
        {language === 'pt' ? 'Saltar para o conteúdo principal' : 'Skip to main content'}
      </a>
      
      <nav className="fixed top-0 left-0 right-0 z-[120] border-b bg-primary border-primary/80">
      <div className="w-full px-4">
        <div className="relative flex items-center justify-between h-20 2xl:grid 2xl:grid-cols-[auto_1fr_auto] 2xl:items-center 2xl:gap-2">
          {/* Mobile menu button - Left */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="inline-flex 2xl:hidden text-white p-2 min-h-[44px] min-w-[44px] items-center justify-center justify-self-start"
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          {/* Logo - Absolutely centered on mobile/tablet/laptop, left on desktop */}
          <div className="flex-shrink-0 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 2xl:static 2xl:translate-x-0 2xl:translate-y-0 mx-auto 2xl:mx-0 2xl:justify-self-start 2xl:col-start-1">
            <Link to={homeUrl}>
              <div className="max-h-[60px] h-auto w-[156px] md:w-[182px] overflow-hidden">
                <img
                  src="/images/kareem-taxi-parceiro-oficial-yango-mocambique.webp"
                  srcSet="/images/kareem-taxi-logo-100.png 100w,
                          /images/kareem-taxi-logo-200.png 200w,
                          /images/kareem-taxi-parceiro-oficial-yango-mocambique.webp 400w"
                  sizes="(max-width: 768px) 156px, 182px"
                  alt="Kareem Taxi - Parceiro Oficial Yango em Moçambique"
                  title="Kareem Taxi - Parceiro Oficial Yango em Moçambique | Transporte Maputo"
                  className="h-auto w-full max-h-full object-contain"
                  loading="eager"
                  fetchPriority="high"
                  width="182"
                  height="71"
                />
              </div>
            </Link>
          </div>

          {/* Desktop Navigation - Center */}
          <div className="hidden 2xl:flex 2xl:col-start-2 2xl:justify-self-start justify-start items-center gap-5 lg:gap-7 2xl:gap-9 min-w-0 overflow-hidden">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="whitespace-nowrap text-white hover:text-red-100 transition-colors font-medium text-[13px] lg:text-sm xl:text-[15px] px-3 py-2 min-h-[44px] flex items-center nav-text-shadow"
              >
                {item.label}
              </a>
            ))}
          </div>

          {/* Desktop Controls - Right */}
          <div className="hidden 2xl:flex 2xl:col-start-3 2xl:justify-self-end items-center gap-2">
            {/* Language Toggle */}
            <div className="flex items-center gap-1">
              <a
                href={getLanguageUrl(location.pathname, 'pt')}
                rel="nofollow"
                className={`text-sm transition-colors px-2 py-2 min-h-[44px] min-w-[44px] flex items-center justify-center ${
                  language === 'pt' ? 'text-white font-bold' : 'text-white font-normal hover:text-white nav-text-shadow'
                }`}
              >
                PT
              </a>
              <span className="text-white/70">|</span>
              <a
                href={getLanguageUrl(location.pathname, 'en')}
                rel="nofollow"
                className={`text-sm transition-colors px-2 py-2 min-h-[44px] min-w-[44px] flex items-center justify-center ${
                  language === 'en' ? 'text-white font-bold' : 'text-white font-normal hover:text-white nav-text-shadow'
                }`}
              >
                EN
              </a>
            </div>
          </div>

          {/* Mobile Language Toggle - Just globe icon */}
          <div className="2xl:hidden relative flex-shrink-0">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsLangDropdownOpen(!isLangDropdownOpen);
              }}
              className="flex items-center justify-center text-white p-2 min-h-[44px] min-w-[44px] hover:bg-white/10 rounded-md transition-colors"
              aria-label="Select language"
              aria-expanded={isLangDropdownOpen}
            >
              <Globe className="w-5 h-5" />
            </button>
            
            {/* Dropdown Menu */}
            {isLangDropdownOpen && (
              <div className="absolute right-0 top-full mt-1 bg-white rounded-md shadow-lg py-1 min-w-[140px] z-50">
                <a
                  href={getLanguageUrl(location.pathname, 'pt')}
                  rel="nofollow"
                  onClick={() => setIsLangDropdownOpen(false)}
                  className={`block px-4 py-3 text-sm hover:bg-gray-100 ${
                    language === 'pt' ? 'text-primary font-bold' : 'text-gray-700'
                  }`}
                >
                  Português
                </a>
                <a
                  href={getLanguageUrl(location.pathname, 'en')}
                  rel="nofollow"
                  onClick={() => setIsLangDropdownOpen(false)}
                  className={`block px-4 py-3 text-sm hover:bg-gray-100 ${
                    language === 'en' ? 'text-primary font-bold' : 'text-gray-700'
                  }`}
                >
                  English
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Navigation - Hidden by default to prevent FOUC */}
        {isMenuOpen && (
          <div className="fixed inset-x-0 top-16 bottom-0 2xl:hidden py-4 px-4 border-t bg-primary border-primary shadow-lg z-[200] overflow-y-auto animate-in slide-in-from-top-4 duration-200">
            <div className="flex flex-col items-start space-y-2">
              {navItems.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={handleNavClick}
                  className="text-left text-white hover:text-red-100 transition-colors font-medium py-3 px-2 min-h-[44px] block nav-text-shadow w-full"
                >
                  {item.label}
                </a>
              ))}

            </div>
          </div>
        )}
      </div>
    </nav>
    </>
  );
};

export default Navigation;